import {useEffect, useState} from "react";
import {Box, Button, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from '@mui/icons-material/Add';
import moment from "moment-timezone";
import {useTranslation} from "react-i18next";

import ShiftAdminAddAmendmentDialog from "./ShiftAdminAddAmendmentDialog";
import DeleteAmendmentDialog from "./DeleteAmendmentDialog";
import AmendmentCard from "../my-history/AmendmentCard";
import {buildShiftServicesLabel, buildAmendmentShiftTimeRange} from "../../helpers/ShiftHelper";
import {FULL_DAY_DATE_FORMAT, MONTH_YEAR_FORMAT} from "../../config/DateTimeConstants";
import {useTimezone} from "../../hooks/useTimezone";
import {SHIFT_ADMIN_VIEWS} from "../../constants/ShiftAdminConstants";


const styles = {
    dialog: {
        closeButton: {
            position: 'absolute',
            right: 20,
            top: 12,
        },
        content: {
            padding: '1rem'
        },
        footerActions: {
            display: 'flex',
            justifyContent: 'flex-start',
            padding: '1rem',
        },
        addAmendmentButton: {
            color: 'grey.600',
            borderColor: 'grey.500',
            "&:hover": {
                backgroundColor: 'grey.200'
            }
        }
    }
};


export default function ShiftAdminPerformerAmendmentsDialog({open, onClose, performer, currentDate, openAddAmendmentOnOpen = false, onAmendmentCreated, activeView}) {
    const {timezone} = useTimezone();
    const {t} = useTranslation();

    const [openAddPerformerDialog, setOpenAddPerformerDialog] = useState(false);
    const [openDeleteAmendmentDialog, setOpenDeleteAmendmentDialog] = useState(false);
    const [selectedAmendment, setSelectedAmendment] = useState(null);

    const dateLabel = currentDate && moment(currentDate).tz(timezone)
        .format(activeView === SHIFT_ADMIN_VIEWS.DAY ? FULL_DAY_DATE_FORMAT : MONTH_YEAR_FORMAT);
    const amendments = performer?.amendments || [];
    const amendmentCount = performer?.amendmentCount ?? amendments.length;
    const amendmentCountLabel = t("common.amendmentCountLabel", {count: amendmentCount});


    const handleOpenAddPerformerDialog = () => {
        setOpenAddPerformerDialog(true);
    };

    const handleCloseAddPerformerDialog = () => {
        setOpenAddPerformerDialog(false);
    };

    const handleOpenDeleteAmendmentDialog = (amendment) => {
        setSelectedAmendment(amendment);
        setOpenDeleteAmendmentDialog(true);
    };

    const handleCloseDeleteAmendmentDialog = () => {
        setOpenDeleteAmendmentDialog(false);
        setSelectedAmendment(null);
    };

    useEffect(() => {
        if (open && openAddAmendmentOnOpen) {
            setOpenAddPerformerDialog(true);
        }
    }, [open, openAddAmendmentOnOpen]);


    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Typography variant="h5" fontWeight={700}>
                        {performer?.fullName}
                    </Typography>

                    <Typography color="text.secondary">
                        {dateLabel} · {amendmentCountLabel}
                    </Typography>
                </DialogTitle>

                <IconButton aria-label="close" onClick={onClose} sx={styles.dialog.closeButton}>
                    <CloseIcon/>
                </IconButton>

                <Divider/>

                <DialogContent sx={styles.dialog.content}>
                    <Stack spacing={2}>
                        {amendments.map((amendment) => (
                            <AmendmentCard
                                key={amendment.id}
                                amendment={amendment}
                                shiftTimeRange={buildAmendmentShiftTimeRange(amendment, timezone)}
                                servicesLabel={buildShiftServicesLabel(amendment)}
                                timezone={timezone}
                                isActionable={true}
                                onDeleteAmendment={handleOpenDeleteAmendmentDialog}/>
                        ))}
                    </Stack>
                </DialogContent>

                <Divider/>
                {
                    activeView === SHIFT_ADMIN_VIEWS.DAY
                    &&
                    <Box sx={styles.dialog.footerActions}>
                        <Button
                            variant="outlined"
                            size="small"
                            sx={styles.dialog.addAmendmentButton}
                            startIcon={<AddIcon/>}
                            onClick={handleOpenAddPerformerDialog}>
                            {t("shiftAdminAddAmendmentDialog.addAmendmentLabel")}
                        </Button>
                    </Box>
                }
            </Dialog>

            <ShiftAdminAddAmendmentDialog
                open={openAddPerformerDialog}
                onClose={handleCloseAddPerformerDialog}
                performer={performer}
                timezone={timezone}
                onAmendmentCreated={onAmendmentCreated}/>

            <DeleteAmendmentDialog
                open={openDeleteAmendmentDialog}
                onClose={handleCloseDeleteAmendmentDialog}
                amendment={selectedAmendment}
                onAmendmentDeleted={onAmendmentCreated}/>
        </>
    );
}